import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Loader2, List, LayoutGrid, DollarSign } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { logError } from "@/lib/structured-log";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { searchWorkspaceDealsServer } from "@/server/deals";
import { createFileRoute } from "@tanstack/react-router";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import {
  DEAL_STATUS_OPTIONS,
  PRICE_RANGE_OPTIONS,
  type PriceRange,
  type DealStatusCode,
} from "@/components/deals/schema";
import { CustomSelectContent } from "@/components/deals/custom-select";
import { DealsTable } from "@/components/deals/table";
import { createDealColumns } from "@/components/deals/table/columns";
import DealDetailSheet from "@/components/deals/detail/sheet";
import type { DealTableItem } from "@/components/deals/table/types";
import type { DealStage } from "@/lib/api/generated/dealStage";
import type { DealSearchItemResponse } from "@/lib/api/generated/dealSearchItemResponse";
import { useKanbanData, KanbanBoard } from "@/components/deals/kanban";
import { InlineSvg } from "@/components/ui/inline-svg";
import searchIcon from "@/assets/icons/search.svg?raw";

/**
 * 价格范围筛选类型
 */
type PriceRangeFilter =
  | { type: "all" }
  | { type: "preset"; value: PriceRange }
  | { type: "custom"; min: number; max: number };

/**
 * 将后端 DealListItemResponse 转换为前端 DealTableItem
 *
 * Backend: apps/api/application/deals/schemas.py:95-109
 *
 * 注意事项：
 * - amount 是字符串类型（后端 Decimal），使用 parseFloat 保持精度
 * - contacts, lastMessage 等字段当前后端未返回，设置为空/undefined
 * - createdBy 信息当前不在 list response 中，需要后续从详情接口获取
 */
// 已迁移到搜索 API，旧的 list 转换函数移除

export const Route = createFileRoute("/workspace/$workspaceSlug/deals/")({
  // Cache deals list for 10 seconds (moderately dynamic data)
  // Similar to campaigns list, short cache for data freshness
  staleTime: 10_000, // 10 seconds
  gcTime: 2 * 60_000, // 2 minutes until garbage collection
  loader: async ({ context, parentMatchPromise }) => {
    const workspaceId = await getWorkspaceIdWithFallback(context, "deals", parentMatchPromise);

    // Initial data via search API (page=1, page_size=10)
    const searchData = await searchWorkspaceDealsServer({
      data: {
        workspaceId,
        request: {
          page: 1,
          page_size: 10,
        },
      },
    });

    return {
      workspaceId,
      deals: searchData.items.map(transformSearchItemToTableItem),
      totalCount: searchData.total,
    };
  },
  component: DealsListPage,
});

/**
 * 格式化自定义金额范围显示
 */
function formatCustomRange(min: number, max: number): string {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  return `${formatter.format(min)} - ${formatter.format(max)}`;
}

function DealsListPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { workspaceId, deals, totalCount } = Route.useLoaderData();
  const [selectedDeal, setSelectedDeal] = useState<DealTableItem | null>(null);
  // Temporary input state - updates as user types, doesn't trigger API
  const [searchInput, setSearchInput] = useState<string>("");
  // Actual search query - only updates when search button is clicked
  const [actualSearchQuery, setActualSearchQuery] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageCount, setPageCount] = useState<number>(Math.max(1, Math.ceil(totalCount / pageSize)));
  const [tableData, setTableData] = useState<DealTableItem[]>(deals);
  const [total, setTotal] = useState<number>(totalCount);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("kanban");

  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  // Ref to store kanban triggerSearch function
  const kanbanTriggerSearchRef = useRef<(() => void) | null>(null);

  // 价格范围筛选状态
  const [priceRangeFilter, setPriceRangeFilter] = useState<PriceRangeFilter>({ type: "all" });
  // Select 打开/关闭状态
  const [priceRangeOpen, setPriceRangeOpen] = useState(false);

  const columns = useMemo(() => createDealColumns(t), [t]);

  /**
   * 处理预设价格范围选择
   */
  const handlePriceRangeChange = (value: string) => {
    if (value === "all") {
      setPriceRangeFilter({ type: "all" });
    } else {
      setPriceRangeFilter({
        type: "preset",
        value: value as PriceRange,
      });
    }
    // TODO: 调用 API 筛选（后端需要先支持 min_value/max_value 参数）
  };

  /**
   * 应用自定义金额范围
   */
  const handleCustomRangeApply = (range: { min: number; max: number }) => {
    setPriceRangeFilter({
      type: "custom",
      min: range.min,
      max: range.max,
    });
    // 关闭 Select 下拉菜单，显示更新后的值
    setPriceRangeOpen(false);
    // TODO: 调用 API 筛选
  };

  return (
    <div className="flex w-full h-full flex-col bg-white">
      <div className="h-[64px] px-6">
        <div className="flex h-full items-center">
          <h1 className="text-[16px] font-semibold text-gray-900">{t("pages:deals.title")}</h1>
        </div>
      </div>
      <Separator className="bg-[#dfdfdf]" />
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        defaultValue="kanban"
        className="flex h-full w-full flex-1 flex-col overflow-hidden gap-0"
      >
        <div className="h-[60px] px-6">
          <div className="flex h-full items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute left-1 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-lg border border-[#dfdfdf] bg-white">
                  <InlineSvg content={searchIcon} />
                </div>
                <Input
                  className="h-9 w-[360px] pl-9"
                  placeholder={t("pages:deals.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      // Trigger search on Enter key
                      setActualSearchQuery(searchInput);
                      if (activeTab === "kanban" && kanbanTriggerSearchRef.current) {
                        kanbanTriggerSearchRef.current();
                      }
                    }
                  }}
                />
              </div>
              <Select
                value={statusFilter ?? ""}
                onValueChange={(v) => setStatusFilter(v || undefined)}
              >
                <SelectTrigger className="h-9 w-[160px]">
                  <div className="flex items-center gap-2">
                    <List className="h-4 w-4 text-muted-foreground" />
                    <SelectValue placeholder={t("pages:deals.status")} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {DEAL_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(`pages:deals.stages.${option.value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={
                  priceRangeFilter.type === "preset"
                    ? priceRangeFilter.value
                    : priceRangeFilter.type === "all"
                      ? "all"
                      : undefined
                }
                onValueChange={handlePriceRangeChange}
                open={priceRangeOpen}
                onOpenChange={setPriceRangeOpen}
              >
                <SelectTrigger className="h-9 w-[200px]">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <SelectValue
                      placeholder={
                        priceRangeFilter.type === "custom"
                          ? formatCustomRange(priceRangeFilter.min, priceRangeFilter.max)
                          : t("pages:deals.priceRange")
                      }
                    />
                  </div>
                </SelectTrigger>
                <CustomSelectContent
                  className="p-0"
                  onCustomRangeApply={handleCustomRangeApply}
                  initialRange={
                    priceRangeFilter.type === "custom"
                      ? { min: priceRangeFilter.min, max: priceRangeFilter.max }
                      : undefined
                  }
                >
                  <SelectItem value="all">{t("pages:deals.allPrices")}</SelectItem>
                  {PRICE_RANGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </CustomSelectContent>
              </Select>
              <Button
                disabled={isLoading}
                onClick={async () => {
                  // Update the actual search query that triggers API calls
                  setActualSearchQuery(searchInput);

                  if (activeTab === "kanban") {
                    // Trigger Kanban search
                    if (kanbanTriggerSearchRef.current) {
                      kanbanTriggerSearchRef.current();
                    }
                  } else {
                    // Handle table search
                    setIsLoading(true);
                    try {
                      const priceRange = buildPriceRangeParam(priceRangeFilter) ?? undefined;
                      const res = await searchWorkspaceDealsServer({
                        data: {
                          workspaceId,
                          request: {
                            search_query: searchInput || undefined,
                            stages: statusFilter
                              ? [statusFilter as unknown as DealStage]
                              : undefined,
                            price_range: priceRange,
                            page,
                            page_size: pageSize,
                          },
                        },
                      });
                      const items = res.items.map(transformSearchItemToTableItem);
                      setTableData(items);
                      setTotal(res.total);
                      setPage(res.page);
                      setPageSize(res.page_size);
                      setPageCount(res.total_pages);
                    } catch (error) {
                      logError("deals.search.frontend.failed", {
                        error: error instanceof Error ? error.message : String(error),
                      });
                      toast.error("Search failed, please try again");
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
              >
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("pages:deals.loading")}
                  </span>
                ) : (
                  <span>{t("pages:deals.search")}</span>
                )}
              </Button>
              <Button
                variant="secondary"
                disabled={isLoading}
                onClick={async () => {
                  if (isLoading) return;
                  setIsLoading(true);
                  try {
                    const nextPage = 1;
                    setSearchInput("");
                    setActualSearchQuery("");
                    setStatusFilter(undefined);
                    setPriceRangeFilter({ type: "all" });
                    setPriceRangeOpen(false);
                    setPage(nextPage);
                    const res = await searchWorkspaceDealsServer({
                      data: {
                        workspaceId,
                        request: {
                          page: nextPage,
                          page_size: pageSize,
                        },
                      },
                    });
                    setTableData(res.items.map(transformSearchItemToTableItem));
                    setTotal(res.total);
                    setPage(res.page);
                    setPageSize(res.page_size);
                    setPageCount(res.total_pages);
                  } catch (error) {
                    logError("deals.reset.frontend.failed", {
                      error: error instanceof Error ? error.message : String(error),
                    });
                    toast.error("Reset failed, please try again");
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                {t("pages:deals.reset")}
              </Button>
            </div>
            <TabsList className="h-9">
              <TabsTrigger value="table">
                <List className="h-4 w-4" />
              </TabsTrigger>
              <TabsTrigger value="kanban">
                <LayoutGrid className="h-4 w-4" />
              </TabsTrigger>
            </TabsList>
          </div>
        </div>
        <Separator className="bg-[#dfdfdf]" />
        <div className="flex flex-1 overflow-hidden">
          <TabsContent className="flex h-full flex-1 overflow-hidden relative" value="table">
            <DealsTable
              data={tableData}
              columns={columns}
              workspaceId={workspaceId}
              totalCount={total}
              initialPageSize={pageSize}
              pageCount={pageCount}
              onRowClick={(deal) => setSelectedDeal(deal)}
              onPageChange={async (nextPage) => {
                if (isLoading) return;
                setIsLoading(true);
                try {
                  setPage(nextPage);
                  const priceRange = buildPriceRangeParam(priceRangeFilter) ?? undefined;
                  const res = await searchWorkspaceDealsServer({
                    data: {
                      workspaceId,
                      request: {
                        search_query: actualSearchQuery || undefined,
                        stages: statusFilter ? [statusFilter as unknown as DealStage] : undefined,
                        price_range: priceRange,
                        page: nextPage,
                        page_size: pageSize,
                      },
                    },
                  });
                  setTableData(res.items.map(transformSearchItemToTableItem));
                  setTotal(res.total);
                  setPage(res.page);
                  setPageSize(res.page_size);
                  setPageCount(res.total_pages);
                } catch (error) {
                  logError("deals.pagechange.frontend.failed", {
                    error: error instanceof Error ? error.message : String(error),
                  });
                  toast.error("Page loading failed, please try again");
                } finally {
                  setIsLoading(false);
                }
              }}
              onPageSizeChange={async (nextSize) => {
                if (isLoading) return;
                const nextPage = 1;
                setIsLoading(true);
                try {
                  setPageSize(nextSize);
                  setPage(nextPage);
                  const priceRange = buildPriceRangeParam(priceRangeFilter) ?? undefined;
                  const res = await searchWorkspaceDealsServer({
                    data: {
                      workspaceId,
                      request: {
                        search_query: actualSearchQuery || undefined,
                        stages: statusFilter ? [statusFilter as unknown as DealStage] : undefined,
                        price_range: priceRange,
                        page: nextPage,
                        page_size: nextSize,
                      },
                    },
                  });
                  setTableData(res.items.map(transformSearchItemToTableItem));
                  setTotal(res.total);
                  setPage(res.page);
                  setPageSize(res.page_size);
                  setPageCount(res.total_pages);
                } catch (error) {
                  logError("deals.pagesize.frontend.failed", {
                    error: error instanceof Error ? error.message : String(error),
                  });
                  toast.error("Page size change failed, please try again");
                } finally {
                  setIsLoading(false);
                }
              }}
            />
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/40">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </TabsContent>
          <TabsContent className="flex flex-1 overflow-hidden" value="kanban">
            <DealsKanbanView
              workspaceId={workspaceId}
              searchQuery={actualSearchQuery}
              priceRangeFilter={priceRangeFilter}
              statusFilter={statusFilter}
              onDealClick={(deal) => setSelectedDeal(deal)}
              onDealEdit={(deal) => {
                // TODO: Implement edit functionality
                toast.info(`Edit functionality for ${deal.dealName} not yet implemented`);
              }}
              onDealDelete={(deal) => {
                // TODO: Implement delete functionality
                toast.info(`Delete functionality for ${deal.dealName} not yet implemented`);
              }}
              onTriggerSearchReady={(triggerSearchFn) => {
                kanbanTriggerSearchRef.current = triggerSearchFn;
              }}
            />
          </TabsContent>
        </div>
      </Tabs>

      <DealDetailSheet
        open={Boolean(selectedDeal)}
        onOpenChange={(open) => {
          if (!open) setSelectedDeal(null);
        }}
        deal={selectedDeal}
        workspaceId={workspaceId}
      />
    </div>
  );
}

interface DealsKanbanViewProps {
  workspaceId: string;
  searchQuery: string;
  priceRangeFilter: PriceRangeFilter;
  statusFilter?: string;
  onDealClick: (deal: DealTableItem) => void;
  onDealEdit?: (deal: DealTableItem) => void;
  onDealDelete?: (deal: DealTableItem) => void;
  onTriggerSearchReady?: (triggerSearch: () => void) => void; // Callback to provide triggerSearch function
}

function DealsKanbanView({
  workspaceId,
  searchQuery,
  priceRangeFilter,
  statusFilter,
  onDealClick,
  onDealEdit,
  onDealDelete,
  onTriggerSearchReady,
}: DealsKanbanViewProps) {
  const priceRange = useMemo(() => buildPriceRangeParam(priceRangeFilter), [priceRangeFilter]);

  const { columns, loadColumn, moveDeal, reorderDealInColumn, isInitialLoading, triggerSearch } =
    useKanbanData({
      workspaceId,
      searchQuery: searchQuery || undefined,
      stages: statusFilter ? [statusFilter as unknown as DealStage] : undefined,
      priceRange: priceRange ?? undefined,
    });

  // Provide triggerSearch function to parent component
  useEffect(() => {
    if (onTriggerSearchReady && triggerSearch) {
      onTriggerSearchReady(triggerSearch);
    }
  }, [triggerSearch, onTriggerSearchReady]);

  const handleColumnLoadMore = useCallback(
    async (stage: DealStatusCode) => {
      await loadColumn(stage);
    },
    [loadColumn],
  );

  const handleStageChange = useCallback(
    (dealId: string, oldStage: DealStatusCode, newStage: DealStatusCode) => {
      moveDeal(dealId, oldStage, newStage);
    },
    [moveDeal],
  );

  // Remove initial loading spinner - show columns immediately with skeleton states

  return (
    <KanbanBoard
      columns={columns}
      isInitialLoading={isInitialLoading}
      onColumnLoadMore={handleColumnLoadMore}
      onCardClick={onDealClick}
      onCardEdit={onDealEdit}
      onCardDelete={onDealDelete}
      onStageChange={handleStageChange}
      onCardReorder={reorderDealInColumn}
      enableSorting={true}
    />
  );
}
function transformSearchItemToTableItem(item: DealSearchItemResponse): DealTableItem {
  const ownerName = item.owner
    ? `${item.owner.first_name || ""} ${item.owner.last_name || ""}`.trim() ||
      (item.owner.email ? String(item.owner.email) : "—")
    : "—";
  const contactName = item.contact
    ? `${item.contact.first_name || ""} ${item.contact.last_name || ""}`.trim() ||
      (item.contact.email ? String(item.contact.email) : "—")
    : undefined;
  return {
    id: item.id,
    dealName: item.name,
    companyId: item.company?.id,
    companyName: item.company?.name ?? "Unknown",
    companyLogoUrl: item.company?.logo_url ?? undefined,
    value: parseFloat(item.value_amount),
    valueCurrency: item.value_currency,
    contact: {
      id: item.contact?.id ?? "",
      name:
        `${item.contact?.first_name || ""} ${item.contact?.last_name || ""}`.trim() ||
        String(item.contact?.email),
      avatarUrl: undefined,
    },
    lastMessageAuthor: item.contact
      ? { id: item.contact.id, name: contactName!, avatarUrl: undefined }
      : undefined,
    lastMessagePreview: item.last_message?.subject ?? "-",
    stage: item.stage as DealStatusCode,
    campaignId: item.campaign?.id ?? undefined,
    campaignName: item.campaign?.name ?? "-",
    createdAt: item.created_at,
    createdBy: {
      id: item.owner?.id ?? "",
      name: ownerName,
      avatarUrl: undefined, // 搜索 API 不提供头像 URL
      role: "user",
    },
  };
}
function buildPriceRangeParam(filter: PriceRangeFilter): [number | string, number | string] | null {
  if (filter.type === "all") return null;
  if (filter.type === "custom") return [filter.min, filter.max];
  const v = filter.value;
  if (v.endsWith("+")) {
    const min = parseInt(v.replace("+", ""), 10);
    // For "50000+" format, return [min, ""] to indicate no upper bound
    // The backend will handle empty string as no upper limit
    return [min, ""];
  }
  const [minStr, maxStr] = v.split("-");
  const min = parseInt(minStr, 10);
  const max = parseInt(maxStr, 10);
  return [min, max];
}
